import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import { FiSearch, FiX, FiCheck, FiTag } from 'react-icons/fi'
import { MarcaItem } from '../../../services/marcas'

interface Props {
  marcas: MarcaItem[]
  selectedMarcas: string[]
  onChange: (marcas: string[]) => void
  label?: string
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
`

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;

  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 16px;
  }

  input {
    width: 100%;
    height: 38px;
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    padding: 0 12px 0 36px;
    color: #ffffff;
    font-size: 13px;
    font-family: inherit;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: #f472b6;
      background: rgba(15, 23, 42, 0.85);
    }

    &::placeholder {
      color: #64748b;
    }
  }
`

const SelectedChipsArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 120px;
  overflow-y: auto;
  padding: 6px;
  background: rgba(15, 23, 42, 0.3);
  border: 1px dashed rgba(244, 114, 182, 0.25);
  border-radius: 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(236, 72, 153, 0.2);
  border: 1px solid rgba(236, 72, 153, 0.4);
  color: #f472b6;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  letter-spacing: 0.3px;

  button {
    background: transparent;
    border: none;
    color: #f472b6;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 0;
    font-size: 13px;

    &:hover {
      color: #f87171;
    }
  }
`

const DropdownList = styled.div`
  max-height: 150px;
  overflow-y: auto;
  background: #0f172a;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`

const DropdownItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 12px;
  font-size: 12px;
  cursor: pointer;
  background: ${(props) => (props.$selected ? 'rgba(236, 72, 153, 0.15)' : 'transparent')};
  color: ${(props) => (props.$selected ? '#f472b6' : '#e2e8f0')};
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  .marca-info {
    display: flex;
    align-items: center;
    gap: 8px;

    .marca-name {
      font-weight: 700;
      letter-spacing: 0.3px;
    }
  }
`

export function MarcaMultipleSelector({ marcas, selectedMarcas, onChange, label }: Props) {
  const [search, setSearch] = useState('')

  const filteredMarcas = useMemo(() => {
    if (!search.trim()) return marcas
    const term = search.toLowerCase().trim()
    return marcas.filter((m) => m.marca.toLowerCase().includes(term))
  }, [marcas, search])

  const toggleSelect = (marcaName: string) => {
    const clean = marcaName.trim()
    if (selectedMarcas.includes(clean)) {
      onChange(selectedMarcas.filter((item) => item !== clean))
    } else {
      onChange([...selectedMarcas, clean])
    }
  }

  return (
    <Container>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label || 'Marcas Vinculadas'} ({selectedMarcas.length === 0 ? 'Todas' : `${selectedMarcas.length} selecionada${selectedMarcas.length === 1 ? '' : 's'}`})
        </span>
        {selectedMarcas.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#f87171',
              fontSize: '11px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Limpar seleção (Todas)
          </button>
        )}
      </div>

      {selectedMarcas.length > 0 && (
        <SelectedChipsArea>
          {selectedMarcas.map((marca) => (
            <Chip key={marca}>
              <FiTag style={{ fontSize: '11px' }} />
              {marca}
              <button type="button" onClick={() => toggleSelect(marca)}>
                <FiX />
              </button>
            </Chip>
          ))}
        </SelectedChipsArea>
      )}

      <SearchWrapper>
        <FiSearch />
        <input
          type="text"
          placeholder="Buscar marcas..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </SearchWrapper>

      <DropdownList>
        {filteredMarcas.length === 0 ? (
          <div style={{ padding: '12px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
            Nenhuma marca encontrada.
          </div>
        ) : (
          filteredMarcas.map((m) => {
            const isSelected = selectedMarcas.includes(m.marca)
            return (
              <DropdownItem
                key={m.marca}
                $selected={isSelected}
                onClick={() => toggleSelect(m.marca)}
              >
                <div className="marca-info">
                  <FiTag style={{ color: isSelected ? '#f472b6' : '#64748b', fontSize: '13px' }} />
                  <span className="marca-name">{m.marca}</span>
                </div>
                {isSelected && <FiCheck style={{ color: '#f472b6', fontSize: '15px', flexShrink: 0 }} />}
              </DropdownItem>
            )
          })
        )}
      </DropdownList>
    </Container>
  )
}

export default MarcaMultipleSelector
